import { IconFractureMultiple } from '@consta/icons/IconFractureMultiple'
import { IconHorizonMultiple } from '@consta/icons/IconHorizonMultiple'
import { IconIntersectionPoints } from '@consta/icons/IconIntersectionPoints'
import { IconLitology } from '@consta/icons/IconLitology'
import { IconMaps } from '@consta/icons/IconMaps'
import { IconMarkers } from '@consta/icons/IconMarkers'
import { IconPolygonMultiple } from '@consta/icons/IconPolygonMultiple'
import { IconRigiss } from '@consta/icons/IconRigiss'
import { IconSaturation } from '@consta/icons/IconSaturation'
import { IconWells } from '@consta/icons/IconWells'
import type { Keys, NonCollapsableWithPrimitive } from 'common/types'

import {
  IconAdd,
  IconAttributeExtraction,
  IconCartography,
  IconCrossCorrelation,
  IconCrossPlot,
  IconDivide,
  IconFilling,
  IconFitting,
  IconHistogram,
  IconLinearTransformation,
  IconMultiply,
  IconSmoothing,
  IconSubtract,
  IconVariogram,
  // FIXME: Циклическая зависимость
  // eslint-disable-next-line import/no-internal-modules
} from '../components/Icons'
import {
  IconColoredFault,
  IconColoredHorizon,
  IconColoredMap,
  IconColoredMarker,
  IconColoredPolygon,
  IconColoredWell,
  IconSeismic2D,
  IconSeismic3D,
  IconShot,
} from './ColoredIcons'

/**
 * Эта конструкция обеспечивает обратную совместимость между иконками инспектора, навигатора и Ghost
 *
 * Краткий экскурс:
 * У нас независимо разрабатывались 3 инструмента, которые так или иначе работают с данными:
 * - навигатор
 * - инспектор
 * - дерево
 *
 * У инспектора было даже две итерации, но это другое
 *
 * Независимая разработка = независимые иконки, которые были сделаны когда как
 *
 * И поскольку мы пытались это все хорошо типизировать, у нас были NavigatorIconType, InspectorIconType и т.д.
 *
 * Но вот пришел момент навигатору переехать из Utils, скоро и картография уедет из Utils, и как то
 * синхронизировать эти иконки (как типы, так и сами иконки) стало бы невозможно от слова совсем.
 *
 * А потому волевым решением все эти иконки вынесены из Utils сюда, убрана строгая типизация иконок (но только здесь,
 * NavigatorIconType и InspectorIconType никуда не делись), убраны (обобщены) дубликаты иконок,
 * некоторые заменены на аналогичные из Consta.
 *
 * Именно потому здесь дубликаты одних и тех же иконок под разными именами
 *
 * // TODO: Избавиться от дубликатов иконок обновив типизацию иконок инспектора и навигатора
 *          Возможно их стоит инвертировать, то есть от этого объекта определить типы иконок
 * // TODO: Вынести больше общих иконок (IconGeoHand, IconGeoCursor, иконки для съемок)
 *          (не обязательно в этот объект; он вообще умереть должен в один момент,
 *           или переродиться во что-то более конкретное)
 */
const ICON_MAP = {
  IconGeoObjSeismic3D: IconSeismic3D,
  IconGeoObjSeismic2D: IconSeismic2D,

  IconGeoObjShot: IconShot,
  IconGeoObjectRigiss: IconRigiss,
  IconGeoObjectLithology: IconLitology,
  IconGeoObjectSaturation: IconSaturation,
  IconIntersectionPoints,

  IconMaps,
  IconInspectorMap: IconColoredMap,

  IconGeoObjectMarkers: IconMarkers,
  IconGeoObjectMarker: IconColoredMarker,
  IconInspectorMarker: IconColoredMarker,

  IconGeoObjectWells: IconWells,
  IconGeoObjectWell: IconColoredWell,

  IconGeoObjHorizons: IconHorizonMultiple,
  IconGeoObjHorizon: IconColoredHorizon,
  IconInspectorHorizon: IconColoredHorizon,

  IconPolygonMultiple,
  IconPolygon: IconColoredPolygon,
  IconInspectorPolygon: IconColoredPolygon,

  IconGeoObjFaults: IconFractureMultiple,
  IconGeoObjFault: IconColoredFault,
  IconInspectorFault: IconColoredFault,

  // inspector actions
  IconInspectorAddition: IconAdd,
  IconInspectorSubtraction: IconSubtract,
  IconInspectorMultiplication: IconMultiply,
  IconInspectorDivision: IconDivide,
  IconInspectorSmoothing: IconSmoothing,
  IconInspectorFilling: IconFilling,
  IconInspectorCrossPlot: IconCrossPlot,
  IconInspectorMapHistogram: IconHistogram,
  IconInspectorSurvey3D: IconSeismic3D,
  IconInspectorSurvey2D: IconSeismic2D,
  IconInspectorCrossCorrelation: IconCrossCorrelation,
  IconInspectorPointset: IconIntersectionPoints,
  IconInspectorAttributeExtraction: IconAttributeExtraction,
  IconInspectorWell: IconColoredWell,
  IconInspectorMapBuilding: IconCartography,
  IconInspectorLinearTransformation: IconLinearTransformation,
  IconInspectorFitting: IconFitting,
  IconInspectorVariogram: IconVariogram,
} as const

export type IconType = Keys<typeof ICON_MAP>
export type MaybeIconType = IconType | NonCollapsableWithPrimitive<string> | undefined

const hasIconOfType = (icon: string): icon is IconType => icon in ICON_MAP

export const getIcon = (icon: MaybeIconType) =>
  icon && hasIconOfType(icon) ? ICON_MAP[icon] : undefined
